/**
 * Frontend Error Logger
 *
 * Centralized structured logging for the frontend application.
 * - Ring-buffer for last N log entries (exportable for support tickets)
 * - Session ID for correlation across page views
 * - Breadcrumb trail for error context
 * - Log-level filtering (debug → info → warn → error)
 * - Rate limiting to prevent log floods
 * - Performance measurement with automatic slow-op detection
 *
 * Usage:
 *   import logger from './utils/logger';
 *   logger.error('API call failed', { endpoint: '/products', error });
 *   logger.warn('Deprecated feature used');
 *   logger.info('User logged in');
 *   logger.getLogBuffer();   // last 200 entries
 *   logger.downloadLogs();   // trigger browser download
 */

const isDevelopment = import.meta.env.MODE === 'development';

// ─── Session ID (stable per tab/window lifetime) ─────────────────────────────
const SESSION_ID = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// ─── Log Levels ──────────────────────────────────────────────────────────────
const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };
const MIN_LEVEL = isDevelopment ? LOG_LEVELS.debug : LOG_LEVELS.warn;

// ─── Ring Buffer ─────────────────────────────────────────────────────────────
const MAX_BUFFER_SIZE = 200;
const _logBuffer = [];

// ─── Breadcrumb Trail ────────────────────────────────────────────────────────
const MAX_BREADCRUMBS = 50;
const _breadcrumbs = [];

// ─── Rate Limiting ───────────────────────────────────────────────────────────
const _rateLimitMap = new Map(); // fingerprint → { count, firstSeen }
const RATE_LIMIT_WINDOW_MS = 10_000; // 10s
const RATE_LIMIT_MAX_PER_WINDOW = 20;

const isRateLimited = (fingerprint) => {
  const now = Date.now();
  const entry = _rateLimitMap.get(fingerprint);
  if (!entry || now - entry.firstSeen > RATE_LIMIT_WINDOW_MS) {
    _rateLimitMap.set(fingerprint, { count: 1, firstSeen: now });
    return false;
  }
  entry.count++;
  return entry.count > RATE_LIMIT_MAX_PER_WINDOW;
};

class FrontendLogger {
  constructor() {
    this.sessionId = SESSION_ID;
  }

  // ── Structured entry builder ──────────────────────────────────────────────
  _createEntry(level, message, context = {}) {
    return {
      timestamp: new Date().toISOString(),
      level,
      sessionId: SESSION_ID,
      message,
      context: this._sanitize(context),
      url: typeof window !== 'undefined' ? window.location.pathname : '',
    };
  }

  _sanitize(context) {
    const clone = { ...context };
    // Strip sensitive keys
    const sensitive = ['password', 'token', 'secret', 'authorization', 'cookie'];
    for (const key of Object.keys(clone)) {
      if (sensitive.includes(key.toLowerCase())) {
        clone[key] = '[REDACTED]';
      }
    }
    // Convert Error to plain object
    if (clone.error instanceof Error) {
      clone.error = { message: clone.error.message, stack: clone.error.stack, name: clone.error.name };
    }
    return clone;
  }

  _pushToBuffer(entry) {
    _logBuffer.push(entry);
    if (_logBuffer.length > MAX_BUFFER_SIZE) _logBuffer.shift();
  }

  _addBreadcrumb(category, message) {
    _breadcrumbs.push({ timestamp: Date.now(), category, message });
    if (_breadcrumbs.length > MAX_BREADCRUMBS) _breadcrumbs.shift();
  }

  // ── Core log methods ──────────────────────────────────────────────────────

  /**
   * Log error message
   */
  error(message, context = {}) {
    const entry = this._createEntry('error', message, context);
    this._pushToBuffer(entry);
    this._addBreadcrumb('error', message);

    if (LOG_LEVELS.error >= MIN_LEVEL) {
      console.error(`[ERROR] ${message}`, context);
    }

    // Always send errors to monitoring
    if (!isDevelopment) {
      this.sendToMonitoring('error', message, context);
    }

    if (context.error instanceof Error) {
      console.error(context.error);
    }
  }

  /**
   * Log warning message
   */
  warn(message, context = {}) {
    const fingerprint = `warn:${message}`;
    if (isRateLimited(fingerprint)) return;

    const entry = this._createEntry('warn', message, context);
    this._pushToBuffer(entry);
    this._addBreadcrumb('warn', message);

    if (LOG_LEVELS.warn >= MIN_LEVEL) {
      console.warn(`[WARN] ${message}`, context);
    }
    if (!isDevelopment) {
      this.sendToMonitoring('warning', message, context);
    }
  }

  /**
   * Log info message
   */
  info(message, context = {}) {
    const entry = this._createEntry('info', message, context);
    this._pushToBuffer(entry);
    this._addBreadcrumb('info', message);

    if (LOG_LEVELS.info >= MIN_LEVEL) {
      console.log(`[INFO] ${message}`, context);
    }
  }

  /**
   * Log debug message (development only)
   */
  debug(message, context = {}) {
    if (LOG_LEVELS.debug < MIN_LEVEL) return;

    const entry = this._createEntry('debug', message, context);
    this._pushToBuffer(entry);

    console.log(`[DEBUG] ${message}`, context);
  }

  /**
   * Send error to monitoring service
   * @private
   */
  sendToMonitoring(level, message, context) {
    if (window.Sentry && window.Sentry.captureException) {
      const error = context.error instanceof Error
        ? context.error
        : new Error(message);

      window.Sentry.withScope((scope) => {
        scope.setLevel(level);
        scope.setTag('sessionId', SESSION_ID);
        scope.setContext('additional', context);
        window.Sentry.captureException(error);
      });
    }

    // Fallback to console
    if (level === 'error') {
      console.error(`[${level.toUpperCase()}] ${message}`, context);
    } else {
      console.warn(`[${level.toUpperCase()}] ${message}`, context);
    }
  }

  /**
   * Log API error with request details
   */
  apiError(endpoint, error, requestData = {}) {
    const context = {
      endpoint,
      error,
      requestData,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
    };
    this.error(`API Error: ${endpoint}`, context);
  }

  /**
   * Log user action for analytics
   */
  action(action, data = {}) {
    this._addBreadcrumb('action', action);

    if (isDevelopment) {
      console.log(`[ACTION] ${action}`, data);
    }

    if (window.gtag) {
      window.gtag('event', action, data);
    }
  }

  /**
   * Group related logs
   */
  group(label, fn) {
    if (isDevelopment) {
      console.group(label);
      fn();
      console.groupEnd();
    } else {
      fn();
    }
  }

  /**
   * Measure and log performance
   */
  async measure(label, fn) {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;

      if (duration > 1000) {
        this.warn(`Slow operation: ${label}`, { duration: `${duration.toFixed(2)}ms` });
      } else {
        this.debug(`Performance: ${label}`, { duration: `${duration.toFixed(2)}ms` });
      }

      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.error(`Failed operation: ${label}`, { error, duration: `${duration.toFixed(2)}ms` });
      throw error;
    }
  }

  // ── Buffer & export utilities ─────────────────────────────────────────────

  /**
   * Get the entire log buffer (structured entries)
   * @param {string} [minLevel] — 'debug'|'info'|'warn'|'error'
   */
  getLogBuffer(minLevel) {
    if (!minLevel) return [..._logBuffer];
    const min = LOG_LEVELS[minLevel] ?? 0;
    return _logBuffer.filter((e) => LOG_LEVELS[e.level] >= min);
  }

  /**
   * Get breadcrumb trail
   */
  getBreadcrumbs() {
    return [..._breadcrumbs];
  }

  /**
   * Download log buffer as JSON file (for support tickets)
   */
  downloadLogs() {
    const data = {
      sessionId: SESSION_ID,
      exportedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      breadcrumbs: this.getBreadcrumbs(),
      logs: this.getLogBuffer(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gabhan-logs-${SESSION_ID}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Clear all buffered logs and breadcrumbs
   */
  clear() {
    _logBuffer.length = 0;
    _breadcrumbs.length = 0;
    _rateLimitMap.clear();
  }
}

const logger = new FrontendLogger();

export default logger;
